// ============================================================================
// ROLE PERMISSION MODALS — PRIMA
// ============================================================================
// RolePermissionFormModal  — create / edit
// RolePermissionViewModal  — read-only details
// ============================================================================

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';

// ============================================================================
// TYPES
// ============================================================================

export interface RolePermissionFormData {
  roleCode: string;
  roleName: string;
  description: string;
  visibleModules: string[];
  moduleActions: Record<string, string[]>;
}

export interface RolePermissionRecord {
  id: string;
  roleCode: string;
  roleName: string;
  description?: string;
  visibleModules: string[];
  moduleActions: Record<string, string[]>;
  isSystemRole: boolean;
  isActive: boolean;
  createdAt: string;
  createdByName?: string;
  updatedAt?: string;
  updatedByName?: string;
  rowVersion: number;
}

// ============================================================================
// CONSTANTS: available actions per module & navigation sections for PRIMA
// ============================================================================

const MODULE_AVAILABLE_ACTIONS: Record<string, string[]> = {
  'overview':                 ['view'],
  'engagement-type-master':   ['add', 'edit', 'delete', 'view'],
  'activity-master':          ['add', 'edit', 'delete', 'view'],
  'client-type-master':       ['add', 'edit', 'delete', 'view'],
  'designation-master':       ['add', 'edit', 'delete', 'view'],
  'location-master':          ['add', 'edit', 'delete', 'view'],
  'client-group-master':      ['add', 'edit', 'delete', 'view'],
  'employee-master':          ['add', 'edit', 'delete', 'view', 'terminate'],
  'holiday-master':           ['add', 'edit', 'delete', 'view'],
  'client-master':            ['add', 'edit', 'delete', 'view'],
  'engagement-management':    ['add', 'edit', 'delete', 'view', 'view-all-employees', 'view-own-team', 'manage-all-employees', 'manage-own-team', 'close', 'reopen'],
  'disbursement-entry':       ['add', 'edit', 'delete', 'view', 'view-all-employees', 'view-own-team', 'manage-all-employees', 'manage-own-team'],
  'timesheet-entry':          ['add', 'edit', 'delete', 'view', 'view-all-employees', 'view-own-team', 'manage-all-employees', 'manage-own-team', 'reopen'],
  'employee-activity-report': ['view', 'export', 'view-all-employees', 'view-own-team'],
  'wip-report':               ['view', 'export', 'view-all-employees', 'view-own-team'],
  'timesheet-status-report':  ['view', 'export', 'view-all-employees', 'view-own-team'],
  'manager-dashboard':        ['view', 'view-all-employees', 'view-own-team'],
  'fsa-engagements':          ['add', 'edit', 'delete', 'view', 'view-all-employees', 'view-own-team', 'manage-all-employees', 'manage-own-team'],
  'fsa-templates':            ['view'],
  'fsa-audit-firms':          ['add', 'edit', 'delete', 'view'],
  'fsa-audit-partners':       ['add', 'edit', 'delete', 'view'],
  'fsa-director-signatories': ['add', 'edit', 'delete', 'view'],
  'fsa-partners':             ['add', 'edit', 'delete', 'view'],
  'role-permissions':         ['add', 'edit', 'delete', 'view'],
  'config-settings':          ['edit', 'view'],
  'fsa-document-output':      ['edit', 'view'],
};

// Action codes that participate in the View-scope ladder.
const VIEW_SCOPE_CODES = ['view-all-employees', 'view-own-team'] as const;
// Action codes that participate in the Action-scope ladder (Create / Update / Delete).
// Only present on transaction modules — reports/dashboards are read-only.
const ACTION_SCOPE_CODES = ['manage-all-employees', 'manage-own-team'] as const;
// CRUD-style verbs rendered as plain checkboxes on the "Actions" row.
const CRUD_ACTION_CODES = ['add', 'edit', 'delete', 'view', 'export'] as const;
// "Other" actions that don't fit either ladder (e.g. timesheet reopen,
// engagement close/reopen). Per-module: which "reopen" label is shown depends
// on the module's available actions list — engagement-management has both
// 'close' and 'reopen' so the labels are engagement-specific; timesheet-entry
// has only 'reopen' meaning the timesheet-reopen variant.
const OTHER_ACTION_LABELS: Record<string, string> = {
  'reopen':         'Reopen',
  'close':          'Close engagement',
  'reset-password': 'Reset password',
  'submit':         'Submit',
  'terminate':      'Terminate employee',
};

type ScopeLevel = 'self' | 'team' | 'all';

const SCOPE_LEVEL_LABEL: Record<ScopeLevel, string> = {
  self: 'Self only',
  team: 'My team',
  all:  'All employees',
};

/** Read the current view-scope level from a module's action array. */
function readViewScope(actions: string[] | undefined): ScopeLevel {
  if (!actions) return 'self';
  if (actions.includes('view-all-employees')) return 'all';
  if (actions.includes('view-own-team')) return 'team';
  return 'self';
}

/** Read the current action-scope level from a module's action array. */
function readActionScope(actions: string[] | undefined): ScopeLevel {
  if (!actions) return 'self';
  if (actions.includes('manage-all-employees')) return 'all';
  if (actions.includes('manage-own-team')) return 'team';
  return 'self';
}

/** Replace any view-scope codes in `actions` with the codes implied by `level`. */
function writeViewScope(actions: string[], level: ScopeLevel): string[] {
  const stripped = actions.filter(a => !VIEW_SCOPE_CODES.includes(a as any));
  if (level === 'all')  return [...stripped, 'view-all-employees'];
  if (level === 'team') return [...stripped, 'view-own-team'];
  return stripped;
}

/** Replace any action-scope codes in `actions` with the codes implied by `level`. */
function writeActionScope(actions: string[], level: ScopeLevel): string[] {
  const stripped = actions.filter(a => !ACTION_SCOPE_CODES.includes(a as any));
  if (level === 'all')  return [...stripped, 'manage-all-employees'];
  if (level === 'team') return [...stripped, 'manage-own-team'];
  return stripped;
}

const MODULE_LABELS: Record<string, string> = {
  'overview':                 'Overview',
  'engagement-type-master':   'Engagement Type',
  'activity-master':          'Activity',
  'client-type-master':       'Client Type',
  'designation-master':       'Designation',
  'location-master':          'Location',
  'client-group-master':      'Client Group',
  'employee-master':          'Employee',
  'holiday-master':           'Holiday',
  'client-master':            'Client',
  'engagement-management':    'Engagement',
  'disbursement-entry':       'Disbursement',
  'timesheet-entry':          'Timesheet',
  'employee-activity-report': 'Employee Activity',
  'wip-report':               'WIP Report',
  'timesheet-status-report':  'Timesheet Status',
  'manager-dashboard':        'Manager Dashboard',
  'fsa-engagements':          'FSA Engagements',
  'fsa-templates':            'Template Browser',
  'fsa-audit-firms':          'Audit Firms',
  'fsa-audit-partners':       'Audit Partners',
  'fsa-director-signatories': 'Directors / Signatories',
  'fsa-partners':             'Partners',
  'role-permissions':         'Role Permissions',
  'config-settings':          'Configuration',
  'fsa-document-output':      'Document Output Settings',
};

const NAV_SECTIONS = [
  { section: 'Dashboard',           modules: ['overview', 'manager-dashboard'] },
  { section: 'Masters',       modules: ['engagement-type-master', 'activity-master', 'client-type-master', 'designation-master', 'location-master', 'client-group-master', 'employee-master', 'holiday-master', 'client-master'] },
  { section: 'Transactions',  modules: ['engagement-management', 'disbursement-entry', 'timesheet-entry'] },
  { section: 'Reports',       modules: ['employee-activity-report', 'wip-report', 'timesheet-status-report'] },
  { section: 'FSA',           modules: ['fsa-engagements', 'fsa-templates', 'fsa-audit-firms', 'fsa-audit-partners', 'fsa-director-signatories', 'fsa-partners'] },
  { section: 'Administration',      modules: ['role-permissions', 'config-settings', 'fsa-document-output'] },
];

// ============================================================================
// FORM MODAL (Create / Edit)
// ============================================================================

interface RolePermissionFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: RolePermissionFormData;
  isSystemRole?: boolean;
  onFormDataChange: (data: RolePermissionFormData) => void;
  onSave: () => void;
  onClose: () => void;
}

export const RolePermissionFormModal: React.FC<RolePermissionFormModalProps> = ({
  isOpen,
  mode,
  formData,
  isSystemRole = false,
  onFormDataChange,
  onSave,
  onClose,
}) => {
  if (!isOpen) return null;

  const title = mode === 'create' ? 'Create Role Permission' : 'Edit Role Permission';
  const isCodeReadOnly = mode === 'edit' || isSystemRole;
  const isValid = formData.roleCode.trim() !== '' && formData.roleName.trim() !== '';

  const isModuleVisible = (moduleId: string) => formData.visibleModules.includes(moduleId);
  const isActionChecked = (moduleId: string, action: string) =>
    formData.moduleActions[moduleId]?.includes(action) ?? false;

  const toggleModuleVisibility = (moduleId: string) => {
    const newVisibleModules = isModuleVisible(moduleId)
      ? formData.visibleModules.filter((m) => m !== moduleId)
      : [...formData.visibleModules, moduleId];

    const newModuleActions = { ...formData.moduleActions };
    if (isModuleVisible(moduleId)) {
      // Turning OFF — clear actions
      delete newModuleActions[moduleId];
    }

    onFormDataChange({ ...formData, visibleModules: newVisibleModules, moduleActions: newModuleActions });
  };

  const toggleAction = (moduleId: string, action: string) => {
    const current = formData.moduleActions[moduleId] || [];
    const newActions = current.includes(action)
      ? current.filter((a) => a !== action)
      : [...current, action];

    const newModuleActions = { ...formData.moduleActions };
    if (newActions.length > 0) {
      newModuleActions[moduleId] = newActions;
    } else {
      delete newModuleActions[moduleId];
    }

    onFormDataChange({ ...formData, moduleActions: newModuleActions });
  };

  /** Set a scope-ladder value (self/team/all) for a module on either the view or action ladder. */
  const setScopeLevel = (moduleId: string, ladder: 'view' | 'action', level: ScopeLevel) => {
    const current = formData.moduleActions[moduleId] || [];
    const updated = ladder === 'view'
      ? writeViewScope(current, level)
      : writeActionScope(current, level);

    const newModuleActions = { ...formData.moduleActions };
    if (updated.length > 0) {
      newModuleActions[moduleId] = updated;
    } else {
      delete newModuleActions[moduleId];
    }
    onFormDataChange({ ...formData, moduleActions: newModuleActions });
  };

  const toggleAllInSection = (modules: string[], selectAll: boolean) => {
    const newVisibleModules = [...formData.visibleModules];
    const newModuleActions = { ...formData.moduleActions };

    for (const moduleId of modules) {
      if (selectAll) {
        if (!newVisibleModules.includes(moduleId)) newVisibleModules.push(moduleId);
        newModuleActions[moduleId] = [...(MODULE_AVAILABLE_ACTIONS[moduleId] || [])];
      } else {
        const idx = newVisibleModules.indexOf(moduleId);
        if (idx >= 0) newVisibleModules.splice(idx, 1);
        delete newModuleActions[moduleId];
      }
    }

    onFormDataChange({ ...formData, visibleModules: newVisibleModules, moduleActions: newModuleActions });
  };

  const isSectionFullySelected = (modules: string[]) =>
    modules.every((m) => isModuleVisible(m));

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* Role Info */}
          <div className="form-grid-2" style={{ marginBottom: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Role Code *</label>
              <input
                className="form-input"
                value={formData.roleCode}
                onChange={(e) => onFormDataChange({ ...formData, roleCode: e.target.value })}
                placeholder="e.g. prima_manager"
                disabled={isCodeReadOnly}
                style={isCodeReadOnly ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Role Name *</label>
              <input
                className="form-input"
                value={formData.roleName}
                onChange={(e) => onFormDataChange({ ...formData, roleName: e.target.value })}
                placeholder="e.g. Manager"
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={formData.description}
              onChange={(e) => onFormDataChange({ ...formData, description: e.target.value })}
              placeholder="Describe the role's purpose…"
              rows={2}
              style={{ resize: 'vertical' }}
            />
          </div>

          {/* Permission Matrix */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text-primary)' }}>
              Permission Matrix
            </h3>

            {NAV_SECTIONS.map((section) => {
              const allSelected = isSectionFullySelected(section.modules);

              return (
                <div key={section.section} style={{ marginBottom: '1.25rem' }}>
                  {/* Section header with select-all toggle */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      padding: '0.4rem 0.6rem',
                      background: 'var(--bg-secondary, rgba(255,255,255,0.03))',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                    onClick={() => toggleAllInSection(section.modules, !allSelected)}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => toggleAllInSection(section.modules, !allSelected)}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {section.section}
                    </span>
                  </div>

                  {/* Module rows */}
                  {section.modules.map((moduleId) => {
                    const visible = isModuleVisible(moduleId);
                    const availableActions = MODULE_AVAILABLE_ACTIONS[moduleId] || [];
                    const moduleActions = formData.moduleActions[moduleId] || [];

                    // Bucket actions by category — CRUD verbs as checkboxes, scope ladders as radios,
                    // anything left over (e.g. 'reopen') as "Other" labelled checkboxes.
                    const crudActions = availableActions.filter(a =>
                      (CRUD_ACTION_CODES as readonly string[]).includes(a),
                    );
                    const otherActions = availableActions.filter(a =>
                      !(CRUD_ACTION_CODES as readonly string[]).includes(a) &&
                      !(VIEW_SCOPE_CODES as readonly string[]).includes(a) &&
                      !(ACTION_SCOPE_CODES as readonly string[]).includes(a),
                    );
                    const showViewLadder = availableActions.some(a =>
                      (VIEW_SCOPE_CODES as readonly string[]).includes(a),
                    );
                    const showActionLadder = availableActions.some(a =>
                      (ACTION_SCOPE_CODES as readonly string[]).includes(a),
                    );
                    const currentViewLevel   = readViewScope(moduleActions);
                    const currentActionLevel = readActionScope(moduleActions);

                    const radioRow = (
                      ladder: 'view' | 'action',
                      currentLevel: ScopeLevel,
                      labelText: string,
                    ) => (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          padding: '0.15rem 0 0.15rem 2.4rem',
                          fontSize: '0.75rem',
                          color: visible ? 'var(--text-secondary)' : 'var(--text-muted)',
                        }}
                      >
                        <span style={{ minWidth: '100px', fontWeight: 500 }}>{labelText}</span>
                        {(['self', 'team', 'all'] as ScopeLevel[]).map((lvl) => (
                          <label
                            key={lvl}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.3rem',
                              cursor: visible ? 'pointer' : 'not-allowed',
                            }}
                          >
                            <input
                              type="radio"
                              name={`${moduleId}-${ladder}`}
                              checked={currentLevel === lvl}
                              onChange={() => setScopeLevel(moduleId, ladder, lvl)}
                              disabled={!visible}
                              style={{ accentColor: 'var(--primary)' }}
                            />
                            {SCOPE_LEVEL_LABEL[lvl]}
                          </label>
                        ))}
                      </div>
                    );

                    return (
                      <div
                        key={moduleId}
                        style={{
                          padding: '0.4rem 0.6rem 0.4rem 1.8rem',
                          opacity: visible ? 1 : 0.5,
                          borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))',
                        }}
                      >
                        {/* Module header — visibility toggle + module name */}
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            marginBottom: '0.25rem',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={visible}
                            onChange={() => toggleModuleVisibility(moduleId)}
                            style={{ accentColor: 'var(--primary)' }}
                          />
                          {MODULE_LABELS[moduleId] || moduleId}
                        </label>

                        {/* CRUD action row (Add / Edit / Delete / View / Export) */}
                        {crudActions.length > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              padding: '0.15rem 0 0.15rem 2.4rem',
                              fontSize: '0.75rem',
                              color: visible ? 'var(--text-secondary)' : 'var(--text-muted)',
                            }}
                          >
                            <span style={{ minWidth: '100px', fontWeight: 500 }}>Actions</span>
                            {crudActions.map((action) => (
                              <label
                                key={action}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  cursor: visible ? 'pointer' : 'not-allowed',
                                  textTransform: 'capitalize',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isActionChecked(moduleId, action)}
                                  onChange={() => toggleAction(moduleId, action)}
                                  disabled={!visible}
                                  style={{ accentColor: 'var(--primary)' }}
                                />
                                {action}
                              </label>
                            ))}
                          </div>
                        )}

                        {/* View-scope ladder */}
                        {showViewLadder && radioRow('view', currentViewLevel, 'View scope')}

                        {/* Action-scope ladder (transactions only) */}
                        {showActionLadder && radioRow('action', currentActionLevel, 'Action scope')}

                        {/* Other actions (e.g. 'reopen' on timesheet) */}
                        {otherActions.length > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              padding: '0.15rem 0 0.15rem 2.4rem',
                              fontSize: '0.75rem',
                              color: visible ? 'var(--text-secondary)' : 'var(--text-muted)',
                              flexWrap: 'wrap',
                            }}
                          >
                            <span style={{ minWidth: '100px', fontWeight: 500 }}>Other</span>
                            {otherActions.map((action) => (
                              <label
                                key={action}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  cursor: visible ? 'pointer' : 'not-allowed',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isActionChecked(moduleId, action)}
                                  onChange={() => toggleAction(moduleId, action)}
                                  disabled={!visible}
                                  style={{ accentColor: 'var(--primary)' }}
                                />
                                {OTHER_ACTION_LABELS[action] || action}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={onSave} disabled={!isValid}>
            <IconSystem.save size={16} />
            {' '}{mode === 'create' ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// VIEW MODAL (Read-Only)
// ============================================================================

interface RolePermissionViewModalProps {
  isOpen: boolean;
  record: RolePermissionRecord | null;
  onClose: () => void;
}

export const RolePermissionViewModal: React.FC<RolePermissionViewModalProps> = ({
  isOpen,
  record,
  onClose,
}) => {
  if (!isOpen || !record) return null;

  const detailStyle: React.CSSProperties = { fontSize: '0.82rem', color: 'var(--text-secondary)' };
  const labelStyle: React.CSSProperties  = { fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.15rem' };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title">Role Permission Details</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* Role info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div>
              <div style={labelStyle}>Role Code</div>
              <div style={detailStyle}>{record.roleCode}</div>
            </div>
            <div>
              <div style={labelStyle}>Role Name</div>
              <div style={detailStyle}>{record.roleName}</div>
            </div>
            <div>
              <div style={labelStyle}>Type</div>
              <div style={detailStyle}>
                <span style={{
                  padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem',
                  background: record.isSystemRole ? 'rgba(99,102,241,0.15)' : 'rgba(156,163,175,0.15)',
                  color: record.isSystemRole ? '#818cf8' : '#9ca3af',
                }}>
                  {record.isSystemRole ? 'System' : 'Custom'}
                </span>
              </div>
            </div>
            <div>
              <div style={labelStyle}>Status</div>
              <div style={detailStyle}>
                <span className={`badge badge-${record.isActive ? 'active' : 'inactive'}`}>
                  {record.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            {record.description && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={labelStyle}>Description</div>
                <div style={detailStyle}>{record.description}</div>
              </div>
            )}
          </div>

          {/* Permission matrix (read-only) */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text-primary)' }}>
              Permission Matrix
            </h3>

            {NAV_SECTIONS.map((section) => {
              const visibleInSection = section.modules.filter((m) => record.visibleModules.includes(m));
              if (visibleInSection.length === 0) return null;

              return (
                <div key={section.section} style={{ marginBottom: '1rem' }}>
                  <div style={{
                    fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)',
                    padding: '0.3rem 0.6rem',
                    background: 'var(--bg-secondary, rgba(255,255,255,0.03))',
                    borderRadius: '6px', marginBottom: '0.4rem',
                  }}>
                    {section.section}
                  </div>

                  {visibleInSection.map((moduleId) => {
                    const actions = record.moduleActions[moduleId] || [];
                    const available = MODULE_AVAILABLE_ACTIONS[moduleId] || [];
                    // Split into pills (CRUD + Other) and resolved scope levels for display.
                    const pillActions = actions.filter(a =>
                      !(VIEW_SCOPE_CODES as readonly string[]).includes(a) &&
                      !(ACTION_SCOPE_CODES as readonly string[]).includes(a),
                    );
                    const showViewLadder = available.some(a =>
                      (VIEW_SCOPE_CODES as readonly string[]).includes(a),
                    );
                    const showActionLadder = available.some(a =>
                      (ACTION_SCOPE_CODES as readonly string[]).includes(a),
                    );
                    const viewLevel   = readViewScope(actions);
                    const actionLevel = readActionScope(actions);

                    return (
                      <div
                        key={moduleId}
                        style={{ padding: '0.3rem 0.6rem 0.3rem 1.8rem' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontSize: '0.82rem', minWidth: '200px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                            {MODULE_LABELS[moduleId] || moduleId}
                          </span>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            {pillActions.map((action) => (
                              <span
                                key={action}
                                style={{
                                  padding: '0.1rem 0.4rem', borderRadius: '3px', fontSize: '0.7rem',
                                  background: 'rgba(99,102,241,0.12)', color: '#818cf8',
                                }}
                              >
                                {OTHER_ACTION_LABELS[action] || action}
                              </span>
                            ))}
                            {pillActions.length === 0 && !showViewLadder && (
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>visible only</span>
                            )}
                          </div>
                        </div>

                        {/* Resolved scope ladders (read-only) */}
                        {(showViewLadder || showActionLadder) && (
                          <div style={{
                            display: 'flex',
                            gap: '1rem',
                            paddingLeft: 'calc(200px + 0.75rem)',
                            marginTop: '0.2rem',
                            fontSize: '0.7rem',
                            color: 'var(--text-muted)',
                          }}>
                            {showViewLadder && (
                              <span>View: <span style={{ color: 'var(--text-secondary)' }}>{SCOPE_LEVEL_LABEL[viewLevel]}</span></span>
                            )}
                            {showActionLadder && (
                              <span>Action: <span style={{ color: 'var(--text-secondary)' }}>{SCOPE_LEVEL_LABEL[actionLevel]}</span></span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};
